// v32 — zdieľané TELO profilu: hero karta (tier ring/glow, meno, Community, level, štatistiky),
// status s reakciami, odznaky (EarnedBadgesCard — rovnaký element ako v Prehľade, bez „Všetky")
// a vyhraté výzvy. Používa ho verejná stránka /u/:id (SocialFeature.PublicProfile) aj popup
// ProfileModal (klik na profilovú fotku / „Zobraziť verejný profil" v Účte) — JEDEN zdroj pravdy.
import { useState } from "react";
import { BuzerIcon } from "../../BuzerIcons";
import { LEVEL_ICON, TIER_ICON } from "../../LevelIcons";
import { REACTIONS } from "../../lib/constants";
import { fmtDur } from "../../lib/format";
import { LEVEL_CATS, getLevel, gname, levelIndex } from "../../lib/levels";
import { st } from "../../styles.js";
import { Avatar } from "./Avatar";

// v33h — tier prezeraného profilu (−1 = Lite/bez levelu → žiadne prefarbenie). Zdieľané s
// ProfileModal a PublicProfile, ktoré ním prefarbia CELÝ panel (Avatar.tierCardStyle).
export function profileTier(p) {
  if (!p || p.lite) return -1;
  const t = typeof p.tier === "number" ? p.tier : getLevel(p.totalPoints || 0).cat;
  // T3 — „Deň nula" (0–4 b, backend tier −1) je skutočný NAJNIŽŠÍ level, nie Lite. Predtým padal do
  // bledej ne-tier karty (iný vzhľad než všetky ostatné levely). Zarovnáme ho na najnižší tier (0 =
  // Nováčik), aby mal rovnaký hero/tier vzhľad ako vyššie levely. Lite (p.lite) ostáva −1 = neutrálny.
  return Math.max(0, t);
}
import { EarnedBadgesCard } from "./BadgeGrid";
import { Glyph } from "./Glyph";
import { Modal } from "./Modal";
import { ProfStat, Rank } from "./atoms";
import { Tag, StateDot } from "./ds";
import { iconChipStyle } from "../../lib/icons-resolve";

// v34 B7 — badge vedľa mena vo verejnom profile: „Community" (premium) / „Solo" (bez Community).
// Biely priesvitný outline na tier profile, čitateľný neutrálny obrys inde (Tag tone="outline").
// Zdieľané: hlavička Sheetu v ProfileModale + meno pod fotkou na stránke /u/:id.
export function ProfileNameBadge({ premium }) {
  return premium
    ? <Tag tone="outline" icon="star2">Community</Tag>
    : <Tag tone="outline" icon="user">Solo</Tag>;
}

export function ProfileBody({ p, onReact, heroAction, hideName = false, highlightReaction = null }) {
  const [badgeInfo, setBadgeInfo] = useState(null);
  // v2.1 / v33h — profil sa mení s tierom. Panel je teraz prefarbený CELÝ podľa tieru (obal —
  // ProfileModal/PublicProfile — mu dá Avatar.tierCardStyle: farba tieru + biele priesvitné vrstvy),
  // takže hero už nemá vlastnú kartu (obsah leží priamo na farbe tieru, ako v LevelUpModal); watermark
  // ikony tieru ostáva. Lite/bez levelu (tier −1) → neutrálny povrch (pôvodná bledá karta).
  const tier = profileTier(p);
  const tiered = tier >= 0;
  // Item 2 — Lite profil má teraz tiež plnú kartu (sivá „grey tier" — obal mu ju dá cez cardStyle),
  // takže hero leží PRIAMO na karte (ako pri tieroch), nie vo vlastnej bledej profileCard karte.
  // `tiered` (biele-na-farbe špecifiká: watermark, reakcie, séria) ostáva len pre skutočné tiery.
  const onCard = tiered || (p && p.lite);
  const tierCat = tiered ? LEVEL_CATS[Math.min(tier, LEVEL_CATS.length - 1)] : null;
  if (!p) return null;
  // sekcie (odznaky/návyky/výzvy) sú priesvitné biele karty — na farbe tieru, alebo bledé (Lite).
  const paleCard = { ...st.card, background: "var(--surface-solid)" };
  // v34 B2/B5 — zdieľaný blok štatistík (séria + „Dnes" tag + najdlhšia; body len non-lite).
  // Renderuje sa v oboch vetvách (lite aj non-lite); Lite vynechá len body a level pilulku.
  const statsBlock = (
    <div style={{ display: "flex", marginTop: 14 }}>
      {/* v34 B2 — namiesto zelenej fajky biely outline Tag „Dnes" (biely na tier profile,
          čitateľný neutrálny obrys na bledom/Lite); tooltip zachovaný */}
      <ProfStat icon="streak" value={p.streak} label="séria"
        badge={p.streakExtendedToday
          ? <Tag tone="outline" icon="check" title="Séria je dnes už predĺžená">Dnes</Tag>
          : null} />
      <ProfStat icon="level" value={p.bestStreak} label="najdlhšia" />
      {!p.lite && <ProfStat icon="points" value={p.totalPoints} label="bodov" />}
    </div>
  );
  return (
    <>
      {/* HERO — na farbe tieru bez vlastnej karty (ako level-up modal); Lite = bledá karta */}
      <div style={{ position: "relative", overflow: "hidden", textAlign: "center",
        ...(onCard
          ? { padding: p.equipped_flair ? "40px 6px 6px" : "6px 6px 8px" }
          : { ...st.profileCard, ...(p.equipped_flair ? { paddingTop: 46 } : {}) }) }}>
        {tierCat && <span aria-hidden style={{ position: "absolute", top: -24, right: -20, transform: "rotate(-12deg)", opacity: 0.14, pointerEvents: "none" }}>
          <BuzerIcon name={TIER_ICON[tierCat.name] || "star2"} size={150} color="var(--text)" /></span>}
        <div style={{ position: "relative", display: "flex", justifyContent: "center" }}>
          <Avatar user={p} size={96} />
        </div>
        {/* v33h — meno je už v hlavičke (titul modalu / TopBar), pod fotkou ho netreba (hideName).
            v34 B7 — Community/Solo badge je vedľa mena (v modale ho dodáva Sheet hlavička; tu pri
            stránke ho ukazujeme vedľa mena pod fotkou), už nie samostatne pod fotkou. */}
        {!hideName && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "center", marginTop: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 700, color: "var(--text)" }}>{p.name}</span>
            <ProfileNameBadge premium={p.premium} />
          </div>
        )}
        {/* v15 §2 — Lite používateľ bez bodov/levelu; v34 B5 — sériu + najdlhšiu ukazuje aj Lite */}
        {p.lite ? (<>
          <Tag tone="neutral" icon="feather" style={{ marginTop: 14 }}>Lite režim · bez bodov</Tag>
          {statsBlock}
        </>) : (<>
          {/* v33h — aktuálny level ako priesvitná biela pilulka na farbe tieru (zladené s LevelUpModal).
              Lite/bez tieru → pilulka na bledom povrchu (--neutral-surface tam nie je prefarbený). */}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10,
            background: "var(--neutral-surface)", border: "1px solid var(--neutral-border)", color: "var(--text)",
            borderRadius: 999, padding: "4px 12px", fontSize: 12.5, fontWeight: 700, fontFamily: "var(--font-mono)" }}>
            <BuzerIcon name={LEVEL_ICON[levelIndex(p.totalPoints) + 1] || "star2"} size={14} color="var(--accent)" /> Lv {levelIndex(p.totalPoints) + 1} · {gname(getLevel(p.totalPoints))}
          </span>
          {statsBlock}
        </>)}
        {heroAction}
        {/* v32.18 — status priamo v hero karte (zjednodušený: bez labelu/dátumu), pod ním reakcie */}
        {p.status && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--neutral-border)" }}>
            {/* status sa MUSÍ zalamovať (dlhý text sa inak orezal cez hero overflow:hidden);
                ikona je inline pred textom, text tečie a wrapuje normálne */}
            <div className="bz-profile-status" style={{ fontSize: 15.5, lineHeight: 1.4, color: "var(--text)" }}>
              {p.status_icon ? <span style={{ marginRight: 4, verticalAlign: "-2px", display: "inline-block" }}><Glyph icon={p.status_icon} size={16} /></span> : null}„{p.status}“
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10, justifyContent: "center" }}>
              {REACTIONS.map(([e, ic]) => {
                const n = (p.reactions || {})[e] || 0;
                const mine = p.my_reaction === e;
                // T7 — reakcia otvorená z notifikácie („profile:<uid>:<emoji>") sa vizuálne zvýrazní
                // (rovnaký „flash" ako pri deep-linku na práve vytvorenú položku).
                const hi = highlightReaction === e;
                if (!p.logged_in && !n) return null;
                return (
                  <button key={e} onClick={() => p.logged_in && onReact && onReact(e)}
                    className={hi ? "bz-flash" : undefined}
                    /* v33h — na farbe tieru je aktívna reakcia priesvitná (nie biela); mimo tieru ostáva ember */
                    style={{ ...st.reactChip, background: "var(--surface-solid)", display: "inline-flex", alignItems: "center", gap: 5, ...(mine ? (tiered ? { background: "var(--neutral-surface-3)", borderColor: "var(--text)" } : st.reactOn) : {}), cursor: p.logged_in ? "pointer" : "default" }}>
                    <BuzerIcon name={ic} size={16} color={mine ? "var(--accent)" : "var(--text2)"} />{n ? <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{n}</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* 2. sekcia — ODZNAKY (rovnaký element ako v Prehľade, bez „Všetky").
          v33h — `flat`: bez orámovanej sekcie (levelBox), ostáva len orámovanie samotných odznakov. */}
      {!p.lite && p.badges && p.badges.some((b) => b.earned) && (
        <div style={{ marginTop: 14 }}>
          <EarnedBadgesCard badges={p.badges} onPick={setBadgeInfo} flat />
        </div>
      )}

      {/* 3. sekcia — NÁVYKY (opt-in vo formulári návyku): 7-dňový pás bodiek + séria */}
      {p.public_habits && p.public_habits.length > 0 && (
        <div style={{ ...paleCard, marginTop: 14 }}>
          <div style={{ ...st.fieldHint, display: "inline-flex", alignItems: "center", gap: 6 }}><BuzerIcon name="target" size={14} color="var(--muted)" /> Návyky · posledných 7 dní</div>
          {p.public_habits.map((h, i) => {
            const n = h.streak || 0;
            const _unitShort = { week: "týž.", month: "mes.", half: "pol r.", year: "r." };
            const streakTxt = _unitShort[h.streak_unit]
              ? `${n} ${_unitShort[h.streak_unit]}`
              : `${n} ${n === 1 ? "deň" : (n >= 2 && n <= 4 ? "dni" : "dní")}`;
            const dayLetter = (k) => ["Ne", "Po", "Ut", "St", "Št", "Pi", "So"][new Date(k + "T00:00:00").getDay()];
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 11, padding: "10px 0", borderTop: i > 0 ? "1px solid var(--border)" : "none" }}>
                <span className="bz-lead bz-lead--circle" style={iconChipStyle(h.icon_color)}><Glyph icon={h.icon} size={19} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.label}</span>
                  <span style={{ display: "flex", gap: 7, marginTop: 6 }}>
                    {(h.week || []).map((done, j) => (
                      <span key={j} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        <StateDot state={done ? "done" : "off"} size={11} />
                        <span style={{ fontSize: 8.5, fontFamily: "var(--font-mono)", color: "var(--muted)" }}>{h.week_days ? dayLetter(h.week_days[j]) : ""}</span>
                      </span>
                    ))}
                  </span>
                </span>
                {/* v33h — na farbe tieru je séria biela (červený streak Tag by bol nečitateľný) */}
                {!p.lite && n > 0 && (tiered
                  ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0, fontFamily: "var(--ff-mono)", fontWeight: 700, fontSize: 12, color: "var(--text)", whiteSpace: "nowrap" }}><BuzerIcon name="flame" size={12} color="var(--text)" />{streakTxt}</span>
                  : <Tag tone="streak" icon="flame">{streakTxt}</Tag>)}
              </div>
            );
          })}
        </div>
      )}

      {badgeInfo && (
        <Modal center title={badgeInfo.name} icon={badgeInfo.earned ? "party" : "lock"} maxWidth={360} onClose={() => setBadgeInfo(null)}>
          <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--text)" }}>{badgeInfo.desc}</div>
          <div style={{ ...st.infoNote, marginTop: 12, marginBottom: 0, display: "inline-flex", alignItems: "center", gap: 7, ...(badgeInfo.earned ? { color: "var(--brand-primary)" } : {}) }}>
            <BuzerIcon name={badgeInfo.earned ? "check" : "lock"} size={15} color="currentColor" />{badgeInfo.earned ? "Získaný odznak" : "Zatiaľ nezískaný"}
          </div>
        </Modal>
      )}

      {/* Item 3 — vyhraté výzvy ukazujeme aj Lite používateľom (backend ich vracia rovnako:
          uzavreté súťaže s výsledkom). Predtým gate `!p.lite` ich skrýval. */}
      {p.competitions && p.competitions.length > 0 && (
        <div style={{ ...paleCard, marginTop: 14 }}>
          <div style={{ ...st.fieldHint, display: "inline-flex", alignItems: "center", gap: 6 }}><BuzerIcon name="trophy" size={14} color="var(--muted)" /> Vyhraté výzvy</div>
          {p.competitions.map((c, i) => (
            <div key={i} style={st.boardRow}>
              {/* v34 B3 — biela ikona výzvy na tier profile (currentColor dedí biely --text), čitateľná inde */}
              <span style={{ fontSize: 20, color: "var(--text)" }}><Glyph icon={c.icon} fallback="medal" size={20} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ ...st.boardName, color: "var(--text)" }}>{c.name}</div>
                <div style={st.taskDesc}>{c.result != null ? (c.result_timer ? fmtDur(c.result) : c.result + " " + c.metric) : "bez výsledku"}</div>
              </div>
              {/* T5 — poradie (medaila/číslo) v neutrálnej farbe textu; „/ N" (počet účastníkov) tiež
                  neutrálne (predtým bronzová token-exempt hodnota), nech ladí so zvyškom profilu v témach */}
              <span style={{ ...st.boardRank, width: "auto", color: "var(--text)" }}>
                {c.rank ? <Rank rank={c.rank} /> : "–"}{c.total > 1 ? <span style={{ color: "var(--text2)" }}>{" / " + c.total}</span> : ""}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* T4 — SPOLOČNÍ PRIATELIA (POSLEDNÁ sekcia). Backend ju vracia len prihlásenému cudziemu
          prezerajúcemu (nie na vlastnom profile) a len keď nejakí existujú → inak sa sekcia vynechá.
          Chip odkazuje na /u/<id> (funguje v modale aj na stránke /u/:id, bez ProfileModalProvider). */}
      {p.common_friends && p.common_friends.length > 0 && (
        <div style={{ ...paleCard, marginTop: 14 }}>
          <div style={{ ...st.fieldHint, display: "inline-flex", alignItems: "center", gap: 6 }}><BuzerIcon name="friends" size={14} color="var(--muted)" /> Spoloční priatelia</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {p.common_friends.map((f) => (
              <a key={f.id} href={"/u/" + f.id} className="bz-common-friend">
                <Avatar user={f} size={20} />
                <span className="bz-common-friend__name">{f.name}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
